import React from 'react'
import styled from 'styled-components'
import { Card } from 'react-bootstrap'
import { BarChart2 } from 'lucide-react'
import { useGetTagsQuery } from '../../../../services/tagApi'
import { getContrastYIQ } from '../../../../utils'
import { useAppSelector } from '../../../../store/hooks'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import { ClassStatistic } from '../../../../types/models/model'
interface ClassStatisticsProps {
  class_statistics: Record<string, ClassStatistic> | null | undefined
}

const StatsCard = styled(Card)`
  background: #2a2a2a;
  border: none;
  border-radius: 12px;
  padding: 1.25rem;
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .card-title {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      width: 18px;
      height: 18px;
      opacity: 0.7;
    }
  }
`

const ClassBar = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 140px;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  margin-bottom: 0.75rem;
  transition: all 0.2s ease;
  border-radius: 6px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`

const BarLabel = styled.div<{ $color: string; $textColor: string }>`
  color: ${(props) => props.$textColor};
  font-size: 0.9rem;
  background-color: ${(props) => props.$color};
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
`

const BarWrapper = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`

const Bar = styled.div<{ $percentage: number; $color: string }>`
  height: 100%;
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${(props) => props.$percentage}%;
    background: ${(props) => props.$color};
    transition: width 0.3s ease;
  }
`

const BarValue = styled.div`
  color: white;
  font-size: 0.9rem;
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .invocations {
    font-weight: 500;
  }

  .confidence {
    font-size: 0.8rem;
    color: #8c8c8c;
  }
`

const TotalInvocations = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #a0a0a0;
  font-size: 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .total-value {
    color: white;
    font-weight: 500;
  }
`

const ClassStatistics: React.FC<ClassStatisticsProps> = ({ class_statistics }) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: projectData } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: tagsData } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: projectData?.datasetid || '',
    },
    { skip: !selectedProjectId || !projectData?.datasetid }
  )

  if (!class_statistics || Object.keys(class_statistics).length === 0) {
    return (
      <StatsCard>
        <Card.Title>
          <BarChart2 /> Prediction Statistics
        </Card.Title>
        <div className="text-muted">No class statistics available</div>
      </StatsCard>
    )
  }

  // Calculate total invocations
  const totalPredictions = Object.values(class_statistics).reduce(
    (sum, stat) => sum + stat.predictions,
    0
  )

  return (
    <StatsCard>
      <Card.Title>
        <BarChart2 /> Prediction Statistics
      </Card.Title>
      {Object.entries(class_statistics).map(([className, stats]) => {
        const tag = tagsData?.tags?.find((t) => t.name === className)
        const color = tag ? `#${tag.color.toString(16).padStart(6, '0')}` : '#4caf50'
        const textColor = getContrastYIQ(color)
        const percentage = (stats.predictions / totalPredictions) * 100

        return (
          <ClassBar key={className}>
            <BarLabel $color={color} $textColor={textColor}>
              {className}
            </BarLabel>
            <BarWrapper>
              <Bar $percentage={percentage} $color={color} />
            </BarWrapper>
            <BarValue>
              <div className="invocations">{stats.predictions} Predictions</div>
              <div className="confidence">
                {(stats.avg_confidence * 100).toFixed(0)}% confidence
              </div>
            </BarValue>
          </ClassBar>
        )
      })}
      <TotalInvocations>
        <span>Total Predictions</span>
        <span className="total-value">{totalPredictions}</span>
      </TotalInvocations>
    </StatsCard>
  )
}

export default ClassStatistics
